import { Helmet } from 'react-helmet-async';
import {
  Target, Zap, ArrowRight, TrendingDown, CheckCircle2,
  LayoutDashboard, Bell, ShieldCheck, GitBranch, MonitorOff,
  LineChart, UsersRound, BrainCircuit, Kanban
} from 'lucide-react';
import { Button, Eyebrow, Reveal, Section, SectionHeader, StatBar, CTAStrip } from '../components/ui';

export default function Home() {
  return (
    <main className="page-enter">
      <Helmet>
        <title>MedAliados | Plataforma B2B para Corretoras de Planos de Saúde Sênior</title>
        <meta name="description" content="Plataforma digital exclusiva para corretoras de saúde. Escale suas vendas de planos MedSênior com tecnologia, leads qualificados e suporte comercial dedicado. Mais de 150 corretoras parceiras." />
        <link rel="canonical" href="https://medaliados.com.br/" />
        <meta property="og:title" content="MedAliados | Plataforma B2B para Corretoras de Planos de Saúde Sênior" />
        <meta property="og:description" content="Plataforma digital exclusiva para corretoras. Tecnologia, leads qualificados e o portfólio de maior demanda do mercado sênior." />
        <meta property="og:url" content="https://medaliados.com.br/" />
      </Helmet>
      {/* HERO */}
      <section className="hero-grid min-h-screen flex flex-col justify-center pt-[80px] pb-0 relative overflow-hidden">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow>Plataforma B2B · Exclusivo para corretoras</Eyebrow>
                <h1 className="font-display font-bold h-hero mt-5 text-verde-escuro text-balance">
                  A Plataforma Digital que{' '}
                  <span className="relative inline-block">
                    Escala
                    <svg className="absolute -bottom-1 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none">
                      <path d="M2 7 Q 50 1 100 5 T 198 4" stroke="#8BC34A" strokeWidth="3" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>{' '}
                  os Resultados da Sua Corretora
                </h1>
                <p className="mt-7 text-[1.12rem] md:text-[1.22rem] text-texto-medio max-w-xl leading-relaxed">
                  Tecnologia, previsibilidade e o portfólio de maior demanda do mercado sênior. Exclusivo para corretoras parceiras.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Button size="lg" icon={ArrowRight} to="/contato">Cadastre Sua Corretora</Button>
                  <Button size="lg" variant="outline" to="/como-funciona">Conheça a Plataforma</Button>
                </div>
                <div className="mt-10 flex items-center gap-4 text-[0.88rem] text-texto-medio">
                  <div className="flex -space-x-2">
                    {["#1A3C2A", "#2E7D32", "#4CAF50", "#8BC34A"].map((c, i) => (
                      <span key={i} className="h-7 w-7 rounded-full border-2 border-fundo-bege" style={{ background: c }} />
                    ))}
                  </div>
                  <span>Mais de <b className="text-verde-escuro">150 corretoras</b> já operam com a MedAliados</span>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={120}>
                <div className="relative">
                  <div className="rounded-2xl bg-fundo-branco border border-borda shadow-card p-5 md:p-6">
                    <div className="flex items-center justify-between pb-4 border-b border-borda">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-verde-claro" />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-verde-escuro/70">Dashboard · ao vivo</span>
                      </div>
                      <LayoutDashboard size={16} className="text-verde-medio" />
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3">
                      {[
                        { label: "Leads · 7d", value: "1.284", change: "↑ 12,4%" },
                        { label: "Conversão", value: "22,8%", change: "↑ 3,1pp" },
                        { label: "Comissão", value: "R$ 47k", change: "↑ 8,9%" },
                      ].map((m, i) => (
                        <div key={i} className="rounded-lg border border-borda p-3">
                          <div className="font-mono text-[10px] text-texto-medio uppercase">{m.label}</div>
                          <div className="font-display font-bold text-2xl text-verde-escuro mt-1">{m.value}</div>
                          <div className="text-[11px] text-verde-medio mt-1">{m.change}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5">
                      <div className="flex items-center justify-between text-[11px] font-mono text-texto-medio mb-2">
                        <span>VENDAS POR SEMANA</span>
                        <span>JAN — MAI</span>
                      </div>
                      <div className="flex items-end gap-1.5 h-24">
                        {[38, 52, 44, 61, 58, 72, 68, 84, 79, 91, 88, 100].map((h, i) => (
                          <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i >= 9 ? '#2E7D32' : '#8BC34A' }} />
                        ))}
                      </div>
                    </div>

                    <div className="mt-5 rounded-lg bg-fundo-bege border border-borda p-3 flex items-center gap-3">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-verde-medio text-fundo-branco">
                        <Bell size={14} />
                      </span>
                      <div className="flex-1">
                        <div className="text-[12px] font-semibold text-verde-escuro">Novo lead qualificado</div>
                        <div className="text-[11px] text-texto-medio">CNPJ verificado · Região metropolitana SP</div>
                      </div>
                      <span className="text-[11px] font-mono text-verde-medio">agora</span>
                    </div>
                  </div>

                  <div className="absolute -bottom-5 -left-5 hidden md:flex items-center gap-2 bg-verde-escuro text-fundo-branco rounded-full px-4 py-2 shadow-card">
                    <ShieldCheck size={16} className="text-verde-lima" />
                    <span className="text-[12px] font-medium">Parceria MedSênior</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

      </section>

      <StatBar items={[
        { value: "150", label: "Corretoras Parceiras" },
        { value: "+5.000", label: "Vidas Ativas" },
        { value: "8", label: "Estados com Presença" },
        { value: "98%", label: "Satisfação" },
      ]} />

      {/* PROBLEMA */}
      <Section bg="bege" className="py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeader
                eyebrow="O cenário"
                title="O teto de vidro que trava o crescimento da sua corretora"
                sub="A maioria das corretoras encontra barreiras estruturais para escalar. Processos manuais, ferramentas desconectadas e ausência de previsibilidade limitam o crescimento, mesmo quando há demanda."
              />
              <div className="mt-8 inline-flex items-center gap-3 px-4 py-3 rounded-full bg-fundo-branco border border-borda">
                <TrendingDown size={18} className="text-dourado" />
                <span className="text-[0.92rem] text-verde-escuro font-medium">A MedAliados foi criada para resolver isso.</span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { Icon: GitBranch, t: "Processos fragmentados", d: "Operação comercial dispersa entre planilhas, e-mails e sistemas desconectados." },
              { Icon: MonitorOff, t: "Sem ferramentas digitais", d: "Falta de plataforma que sustente o ciclo completo de conversão." },
              { Icon: LineChart, t: "Baixa previsibilidade", d: "Vendas sazonais, com pouca visibilidade sobre o pipeline e a carteira." },
              { Icon: UsersRound, t: "Escala impossível", d: "Cada novo cliente exige mais esforço manual — o crescimento não compõe." },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full bg-fundo-branco rounded-xl border border-borda p-6 hover:border-verde-medio hover:shadow-soft transition-all">
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-verde-escuro/5 text-verde-medio shrink-0">
                      <c.Icon size={22} />
                    </span>
                    <div>
                      <h3 className="font-display font-semibold text-[1.08rem] text-verde-escuro">{c.t}</h3>
                      <p className="mt-2 text-[0.94rem] text-texto-medio leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* SOLUÇÃO */}
      <Section bg="branco" className="py-24 md:py-32">
        <div className="text-center">
          <Reveal>
            <SectionHeader align="center" eyebrow="A revolução MedAliados"
              title="Três pilares que reescrevem a equação comercial da sua corretora"
              sub="Foco vertical, inteligência de vendas e gestão de oportunidades em uma única plataforma."
            />
          </Reveal>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", Icon: Target, t: "Foco Exclusivo", d: "Venda de planos MedSênior para o mercado 49+ — o segmento de maior crescimento e demanda no Brasil." },
            { n: "02", Icon: BrainCircuit, t: "Inteligência Comercial", d: "Suporte e inteligência de vendas em tempo real, com dados acionáveis e roteiros de conversão." },
            { n: "03", Icon: Kanban, t: "Gestão de Oportunidades", d: "Geração e gestão inteligente de leads qualificados — sua equipe atende quem realmente quer fechar." },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="h-full rounded-2xl bg-fundo-bege border border-borda p-7 md:p-8 hover:border-verde-medio transition-all hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-widest text-verde-medio">{p.n}</span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-verde-escuro text-fundo-branco">
                    <p.Icon size={18} />
                  </span>
                </div>
                <h3 className="font-display font-bold text-[1.5rem] text-verde-escuro mt-6">{p.t}</h3>
                <p className="mt-3 text-[0.98rem] text-texto-medio leading-relaxed">{p.d}</p>
                <div className="mt-6 pt-6 border-t border-borda flex items-center gap-2 text-verde-medio">
                  <span className="text-[0.9rem] font-medium">Pilar estrutural</span>
                  <CheckCircle2 size={16} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <Button variant="outline" size="lg" icon={ArrowRight} to="/como-funciona">Saiba como funciona</Button>
        </div>
      </Section>

      {/* CTA FINAL */}
      <CTAStrip
        headline="Sua corretora está pronta para o próximo nível?"
        sub="Em uma conversa de 20 minutos, mostramos como integrar sua operação à plataforma e iniciar o processo de escala."
        label="Fale com Nossa Equipe"
      />
    </main>
  );
}
